import { Patient, PatientSearch, PatientSearchInterfaces, Pet, PetOwner, PetProfileFormValues } from '@app/core/models';
import { PetOwnerFormValues } from '@app/shared/components/pet-owner-form/pet-owner-form';
import { SaveConsultationPopinFormValuesOutput } from '@app/shared/components/share-reco-popin/share-reco-popin';
import { Helper, LifestageType, SpeciesCode } from '@app/shared/utils';
import { ReproductionStatusCode } from '@app/shared/utils/enums/reproduction-status-code.enum';
import { translateKey } from '@app/shared/utils/static-helpers/translate';

export function getWLPAvailabilityError(lifeStage?: LifestageType): string | null {
  if (Helper.YOUNG_LIFESTAGES.includes(lifeStage)) {
    return translateKey('identification_error_wlp');
  }
  return null;
}

export function getWLPAvailabilityWarning(reproductionStatusCode?: ReproductionStatusCode): string | null {
  if ([ReproductionStatusCode.Gestation, ReproductionStatusCode.Lactation].includes(reproductionStatusCode)) {
    return translateKey('alert_female-gestation-lactation');
  }
  return null;
}

export function getRDAvailabilityError(speciesCode?: SpeciesCode, date?: Date): string | null {
  if (speciesCode !== SpeciesCode.Cat) {
    return translateKey('identification_error_rd');
  } else if (Helper.getPetAgeObject(date)?.years < 7) {
    return translateKey('rd_warning_pet-too-young');
  }
  return null;
}

export function formatPatientSearch(patient: any): PatientSearchInterfaces {
  return {
    owner: patient.contact
      ? {
          addresses: patient.contact.addresses || [],
          email: patient.contact.email || '',
          familyName: patient.contact.familyName || '',
          givenName: patient.contact.givenName || '',
          _id: patient.contact._id || '',
        }
      : null,
    pet: patient.pet || {},
    id: patient.id || '',
    petId: patient.petId || '',
    lastConsultation: patient.lastConsultation
      ? {
          date: patient.lastConsultation.date,
          id: patient.lastConsultation.id,
          patientId: patient.lastConsultation.patientId,
        }
      : undefined,
  };
}

export function formatProfileFormValuesToPet(profile: PetProfileFormValues): Pet {
  return {
    name: profile.name,
    genderCode: profile.gender,
    speciesCode: profile.speciesCode,
    breedCode: profile.breed,
    birth: {
      date: profile.birthdate,
    },
    neutered: profile.neutered,
    reproductionStatusCode: profile.reproductionStatus,
    lifeStage: profile.lifestage,
  };
}

export function formatOwnerFormValuesToOwner(owner: PetOwnerFormValues | SaveConsultationPopinFormValuesOutput): PetOwner {
  return (
    owner.existingOwner || {
      familyName: owner.familyName,
      givenName: owner.givenName,
      email: owner.email,
      telephone: 'telephone' in owner ? owner.telephone : '',
    }
  );
}

export function formatOwnerToOwnerFormValues(owner: PetOwner, isExistingPetOwner = true): PetOwnerFormValues {
  return {
    familyName: owner?.familyName,
    givenName: owner?.givenName,
    telephone: owner?.telephone,
    email: owner?.email,
    existingOwner: isExistingPetOwner ? owner : null,
    consent: false,
    noEmail: false,
  };
}

const buildPetApiBody = (pet: Pet, vetId?: string, country?: string, includeCountryTypeAndAppCode?: boolean): Pet => {
  const petApiBody: Pet = {
    breedCode: pet?.breedCode,
    genderCode: pet?.genderCode,
    neutered: pet?.neutered,
    petActivityCode: pet?.petActivityCode,
    reproductionStatusCode: pet?.reproductionStatusCode,
    weight: pet?.weight,
    idealBodyWeight: pet?.idealBodyWeight,
    vetId,
    birth: pet?.birth,
    name: pet?.name,
    photo: pet?.photo,
    speciesCode: pet?.speciesCode,
  };
  if (includeCountryTypeAndAppCode) {
    petApiBody.country = country;
    petApiBody.type = 'clinic';
    petApiBody.appCode = 'VS';
  }
  return petApiBody;
};

export const buildPatientApiBody = (
  pet: Pet,
  petOwner?: PetOwner,
  clinicId?: string,
  vetId?: string,
  country?: string,
  petShouldIncludeCountryTypeAndAppCode?: boolean,
) => {
  return {
    clinicId,
    contactId: petOwner?.id,
    vetId,
    ...(pet?.id ? { petId: pet?.id } : { pet: buildPetApiBody(pet, vetId, country, petShouldIncludeCountryTypeAndAppCode) }),
  } as Patient;
};

export const mapPatientToPatientSearch = (patient: Patient): PatientSearch => {
  return {
    owner: {
      addresses: patient.contact.addresses,
      email: patient.contact.email,
      familyName: patient.contact.familyName,
      givenName: patient.contact.givenName,
      _id: patient.contact.id,
    },
    id: patient.id,
    pet: {
      genderCode: patient.pet.genderCode,
      name: patient.pet.name,
      speciesCode: patient.pet.speciesCode,
    },
    petId: patient.petId,
  };
};

export const patientSearchNewPetId = 'newPet';
